import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Configuration } from 'src/app/app.constants';
import { Employee } from '../models/employee';
import { CachingService } from './caching.service';
import { NotificationItemViewModel } from '../viewModels/notificationItemViewModel';

@Injectable({
  providedIn: 'root'
})
export class NotificationsService {
  private _notifications = new BehaviorSubject<NotificationItemViewModel[]>([]);
  private _unreadCount = new BehaviorSubject<number>(0);
  private _loading = new BehaviorSubject<boolean>(false);
  private actionUrl: string = '';
  private employee: Employee;
  private _notificationsLoaded = false;

  public notifications = this._notifications.asObservable();
  public unreadNotificationsCount = this._unreadCount.asObservable();
  public loading = this._loading.asObservable();
  public forceShowNotifications = false;

  constructor(private http: HttpClient, private cachingService: CachingService, private configuration: Configuration) {
    const account = this.cachingService.GetAccountInfo();
    if (account) this.employee = this.cachingService.GetEmployee(account.id);
    this.actionUrl = this.configuration.buildEndpoint('Notification/');
  }

  // Set unread count from app component initialization
  setUnreadCount(count: number) {
    this._unreadCount.next(count);
  }

  // Increment unread count when new notification received via SignalR
  incrementUnreadCount() {
    const currentCount = this._unreadCount.getValue();
    this._unreadCount.next(currentCount + 1);
  }

  // Load notifications only when notifications center is opened
  loadNotificationsIfNeeded() {
    if (!this._notificationsLoaded) {
      this.getNotifications();
    }
  }

  addNotifications(notifications: NotificationItemViewModel[]) {
    if (this._notificationsLoaded) {
      // If notifications are already loaded, add to existing list
      const current = this._notifications.getValue();
      current.unshift(...notifications);
      this.updateValues(current);
    } else {
      // If notifications not loaded yet, just increment count
      this.incrementUnreadCount();
    }
  }

  private updateValues(notifications: NotificationItemViewModel[]) {
    this._notifications.next(notifications);
    const unread = notifications.filter((c) => !c.isRead) || [];
    this._unreadCount.next(unread.length);
  }

  markAsRead(notification: NotificationItemViewModel) {
    this.http.put<NotificationItemViewModel>(`${this.actionUrl}Update`, notification).subscribe(() => {
      const notifications = this._notifications.getValue();
      const matching = notifications.find((n) => n.id === notification.id);

      if (matching) {
        matching.isRead = true;
      }

      this.updateValues(notifications);
    });
  }

  markAsUnread(notification: NotificationItemViewModel) {
    this.http.put<NotificationItemViewModel>(`${this.actionUrl}Update`, notification).subscribe(() => {
      const notifications = this._notifications.getValue();
      const matching = notifications.find((n) => n.id === notification.id);

      if (matching) {
        matching.isRead = false;
      }

      this.updateValues(notifications);
    });
  }

  markAllRead() {
    this.http.get<void>(`${this.actionUrl}MarkAllAsRead`).subscribe(() => {
      const notifications = this._notifications.getValue();
      notifications.forEach((n) => (n.isRead = true));

      this.updateValues(notifications);
    });
  }

  clear(notification: NotificationItemViewModel) {
    this.http.put<NotificationItemViewModel>(`${this.actionUrl}Update`, notification).subscribe(() => {
      const notifications = this._notifications.getValue();
      const matching = notifications.find((n) => n.id === notification.id);

      if (matching) {
        notifications.splice(notifications.indexOf(matching), 1);
      }

      this.updateValues(notifications);
    });
  }

  clearAll() {
    this.http.get<void>(`${this.actionUrl}ClearAll`).subscribe(() => {
      const notifications: NotificationItemViewModel[] = [];
      this.updateValues(notifications);
    });
  }

  /// Loads the notifications from the provided array after initial app load
  loadNotifications(notifications: NotificationItemViewModel[]) {
    this._notificationsLoaded = true;
    this.updateValues(notifications);
  }

  getNotifications() {
    if (!this.employee) {
      const account = this.cachingService.GetAccountInfo();
      if (account) this.employee = this.cachingService.GetEmployee(account.id);
    }

    this._loading.next(true);
    this.http.get<NotificationItemViewModel[]>(`${this.actionUrl}List`).subscribe((notifications) => {
      this._notificationsLoaded = true;
      this.updateValues(notifications);
      this._loading.next(false);
    });
  }

  // Reset loaded state when needed (e.g., on logout)
  resetNotificationsState() {
    this._notificationsLoaded = false;
    this._notifications.next([]);
    this._unreadCount.next(0);
  }
}


